import { map, catchError, firstValueFrom } from 'rxjs';
import {
  Component,
  Inject,
  forwardRef,
  HostListener,
  ViewChild,
  ElementRef,
  OnInit,
  AfterViewChecked,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../service/dashboard.service';
import { ThemeService } from '../service/theme.service';
import { IotService } from '../service/iot.service';
import { ChartService } from '../service/chart.service';
import { CognitoService } from '../service/cognito.service';
import { ReportsService, Bin } from '../service/reports.service';
import { BinsService } from '../service/bins.service';
import { TranslateService } from '@ngx-translate/core';
import { BinUsage } from '../constants/bin-usage';
import { BinArrayItem } from '../service/dashboard.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { CsvService } from '../service/csv.service';
import { SystemMessageService } from '../service/system-message.service';
import { LocalStorageService } from '../local-storage.service';

interface collection {
  name: string;
  value: number;
}
interface BinData {
  bin_id: string;
  bin_gps?: string;
  bin_location: string;
  thing_name: string;
  bin_usage: string;
  bin_address: string;
  legal_name: string;
  market_segment: string;
  threshold: number;
  bin_height: number;
  total_volume: number;
}

@Component({
    selector: 'app-device-statistics',
    templateUrl: './device-statistics.component.html',
    styleUrls: [
        './device-statistics.component.css',
        '../../charts.css',
        '../../global-elements.css',
    ],
    standalone: false
})
export class DeviceStatisticsComponent implements OnInit, AfterViewChecked {

  constructor(
    public theme: ThemeService,
    public systemMessage: SystemMessageService,
    public dashboard: DashboardService,
    public devices: IotService,
    public charts: ChartService,
    public cognitoService: CognitoService,
    public reports: ReportsService,
  public bins: BinsService,
  public iot: IotService,
  public http: HttpClient,
  private route: ActivatedRoute,
  public router: Router,
  public csv: CsvService,
  private renderer: Renderer2,
  private localStorageService: LocalStorageService,
  @Inject(forwardRef(() => TranslateService))
  private translate: TranslateService
  ) {}

  public series: any = [];
  public statisticOptions = [{ label: 'collections', value: 'Collections' }];
  public seriesBat: any = [];
  public seriesVcc: any = [];
  public color: string = 'black';
  public binData: Bin = {
    above_ground: 0,
    active: 0,
    bin_address: '',
    bin_depth: 0,
    bin_height: 0,
    bin_id: '',
    bin_gps: '',
    bin_location: '',
    bin_model_number: '',
    bin_postal_code: '',
    bin_shape: '',
    bin_usage: '',
    bin_width: 0,
    client_id: '',
    distributor_id: '',
    thing_name: '',
    bin_volume: 0,
  };
  public loading: boolean = true;
  public maxDaysWithNoCollections: number = 0;
  public minDaysWithNoCollections: number = 0;
  isSmallScreenWidth: boolean = this.getScreenWidth() <= 859;
  public cubicMetersOfWasteCollected: number = 0;
  public relationships: BinArrayItem[] = [];
  public bat: string = '';
  public timestamp: number = 0;
  public totalCollections: number = 0;
  public collections: collection[] = [];
  public statistic = 'Collections';
  public startDate = this.reports.getFirstDateOfTheCurrentYear();
  public endDate = this.reports.getCurrentDate();
  public thingName = '';
  public binThreshold = '';
  public totalWasteCollected = 0;
  public tonnage = 0;
  public returnedResult = true;
  public validFillRate = true;
  @ViewChild('details') detailsElement!: ElementRef;
  @ViewChild('battery') batteryRef!: ElementRef;
  @ViewChild('VCC') VCCRef!: ElementRef;

  async ngOnInit() {
    await this.cognitoService.confirmValidUser();
    // Get the user type with his client or distributor id
    await this.cognitoService.getUserType();

    this.thingName = this.route.snapshot.params['thing'];
    const statistic = this.route.snapshot.params['report']; //Unimplemented
    const start = this.route.snapshot.params['start'];
    const end = this.route.snapshot.params['end'];

    if (
      start !== undefined &&
      (new Date(start) < new Date(this.endDate) ||
        new Date(start) < new Date(end))
    ) {
      this.startDate = start;
    }

    if (end !== undefined && new Date(end) > new Date(this.startDate)) {
      this.endDate = end;
    }

    //Fetch Array of device relationships
    await firstValueFrom(this.getRelationships());

    //Get Bin Data for a device
    await firstValueFrom(await this.reports.getBinByDevice(this.thingName)).then(
      (response: any) => {
        for (const row of response) {
          //Set Bin Data object for device
          this.binData.above_ground = row.above_ground;
          this.binData.active = row.active;
          this.binData.bin_address = row.bin_address;
          this.binData.bin_depth = row.bin_depth;
          this.binData.bin_height = row.bin_height;
          this.binData.bin_width = row.bin_width;
          this.binData.bin_shape = row.bin_shape;
          this.binData.bin_id = row.bin_id;
          this.binData.bin_gps = row.bin_gps;
          this.binData.bin_location = row.bin_location;
          this.binData.bin_model_number = row.bin_model_number;
          this.binData.bin_postal_code = row.bin_postal_code;
          this.binData.bin_usage = row.bin_usage;
          this.binData.bin_volume = row.total_volume;
        }
      }
    );

    //Get Historical Data for a device
    this.reports.historyArray = await this.reports.getHistoryBatch([this.thingName], this.reports.dateStringToUnix(this.startDate), this.reports.dateStringToUnix(this.endDate))

    if(this.reports.historyArray.length === 0){
      this.loading = false;
      this.reports.showGraphs();
      //if collection data empty
      this.returnedResult = false; //show different UI for no results
      return; //exits function
    }

    await Promise.all([
      await this.initLineChart(),
      await this.initCollections(),
      await this.initBatAndVccLineChart(),
    ])

    //Get current screen width on init
    const currentScreenWidth = this.getScreenWidth();
    const currentScreenHeight = this.getScreenHeight();
    // Check the screen width and update the isSmallScreen flag accordingly
    this.isSmallScreenWidth = currentScreenWidth <= 859;

    //Adjust chart parameters for view size
    this.adjustView(currentScreenWidth, currentScreenHeight);

    this.loading = false;
    this.reports.showGraphs();
  }

  ngAfterViewChecked() {
    this.toggleDisableClass(this.detailsElement);
  }

  // Function that return the highest timestamp of the array
  filterHighestDstTimestamp(array: any[]): any {
    const yearStart = new Date(this.startDate);
    const yearEnd = new Date(this.endDate);

    // Filter the array for rows within the year 2023
    const filteredArray = array.filter(
      (row) =>
        new Date(row.dst_timestamp * 1000) >= yearStart &&
        new Date(row.dst_timestamp * 1000) < yearEnd
    );

    // Check if there are rows within the year 2023
    if (filteredArray.length === 0) {
      return null; // No rows within the year 2023
    }

    // Find the row with the highest dst_timestamp
    const highestTimestampRow = filteredArray.reduce((maxRow, currentRow) =>
      currentRow.dst_timestamp > maxRow.dst_timestamp ? currentRow : maxRow
    );

    return highestTimestampRow;
  }

  //Initialize data for Line Chart and Required Parameters for device health
  async initLineChart() {
    //Get Translated Label for Line Chart
    const yAxisLabel = await this.charts.getChartLabel('yAxisLineChartLabel');

    //Set Line Chart Default dimensions
    this.charts.viewLineChartAdmin = [600, 450];

    //Get Last Row of History array (Most Recent Shadow Data Entry)
    const lastRow = this.reports.historyArray.length - 1;

    //Set Class Parameters for Device Health Check
    this.bat = this.reports.historyArray[lastRow].bat;
    this.timestamp = this.reports.historyArray[lastRow].dst_timestamp;

    const start = new Date(this.startDate);
    start.setUTCHours(5, 0, 0, 0);
    const end = new Date(this.endDate);
    end.setUTCHours(5, 0, 0, 0);

    //X Axis Label
    this.charts.xAxisLabelLineChartAdmin = `${start.toLocaleDateString(
      this.charts.getDateLanguage(),
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
    )} - ${end.toLocaleDateString(this.charts.getDateLanguage(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })}`;

    for (let row of this.reports.historyArray) {
      //Format Timestamps
      const date = this.formatDate(row.dst_timestamp);
      if (this.isDateIn(date)) {
        //Format Timestamps
        const formattedDate = date.toLocaleDateString(
          this.charts.getDateLanguage(),
          {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }
        );

        //Line Chart Row Data
        this.series.push({
          name: formattedDate,
          value: (1 - (row.dst / this.binData.bin_height)) * 100,
        });
      }
    }

    if (this.binData.bin_height) {
      //Compile Line Chart Data
      this.charts.singleLineChart = [
        {
          name: this.thingName,
          series: this.series,
        },
      ];
    } else {
      this.validFillRate = false;
    }

    //Set Y Axis Label
    this.charts.yAxisLabelLineChartAdmin = yAxisLabel;
  }

  // Function used to change a date into a unix timestamp
  dateToUnixTimestamp(dateString: string): number {
    // Parse the date string into a Date object
    const date = new Date(dateString);

    // Get the UTC timestamp in milliseconds
    const timestampInMilliseconds = date.getTime();

    return Math.floor(timestampInMilliseconds / 1000);
  }

  //Initialize Data for both Bar Charts
  async initCollections() {
    //Declare function variables
    let collectionCount = 0, //to count collection for the month
      totalCollections = 0, //total collection count
      barChartRow: any[] = [], // array for monthly collects
      noCollectionCountArray: any[] = [], //to save days with no collection for graphs
      noCollectionsCount: number[] = [], //to saves days with no collection for min/max
      currentRow, //to save current row
      previousRow, //to save previous row
      currentDate: Date, //to save current date
      formattedDate; //to save formatted current date

    //Set Color Pallet for Horizontal Bar Chart
    this.charts.colorSchemeBarChartAdmin.domain = [
      '#1f77b4',
      '#ff7f0e',
      '#2ca02c',
      '#d62728',
      '#9467bd',
      '#8c564b',
      '#e377c2',
      '#7f7f7f',
      '#bcbd22',
      '#17becf',
      '#ff9896',
      '#aec7e8',
    ];

    this.charts.xAxisLabelBarChartAdmin = await this.charts.getChartLabel(
      'xAxisCollectionBarChartLabel'
    );
    this.charts.showXAxisLabelBarChartAdmin = false;
    this.charts.showLegendBarChartAdmin = false;
    this.charts.viewBarChartAdmin = [600, 450];


    //Initialize values for iteration
    let daysWithNoCollectionCount = 0;

    let start = this.dateToUnixTimestamp(this.startDate),
      end = this.dateToUnixTimestamp(this.endDate);

    const res = await this.reports.getReportData([this.thingName], start, end);
    const collectionData = JSON.parse(res.collections); // get the thing collection data
    if (!(collectionData.length > 0)) {
      //if collection data empty
      this.returnedResult = false; //show different UI for no results
      return; //exits function
    }

    ////////////// Monthly collection chart ///////////////////////
    for (let index = 0; index < collectionData.length; index++) {
      //for each collection in the collection data set
      currentRow = collectionData[index]; //get the current collection
      previousRow = index > 0 ? collectionData[index - 1] : null; //get the previous collecton if it exixts

      currentDate = new Date(currentRow.preceding_timestamp * 1000); //get the date of the current collection

      formattedDate = currentDate.toLocaleDateString('en-CA', {
        //formats the current date month/year
        year: 'numeric',
        month: 'short',
      });

      if (currentRow) collectionCount++, totalCollections++; // Increment collectionCount and totalCollection for each row
      if (previousRow) {
        //if previous row exixts
        const previousDate = new Date(previousRow.preceding_timestamp * 1000); //get previous date
        if (!this.isInSameMonth(currentDate, previousDate)) {
          //if current and previous dates are not in the same month
          barChartRow.push({ name: formattedDate, value: collectionCount }); // Push the data into the array with the current collectionCount
          collectionCount = 0; // Reset collectionCount for the new month
        } else if (this.isInSameMonth(currentDate, previousDate)) {
          //if current and previous dates are in the same month
          collectionCount++; //increment the count
          barChartRow.push({ name: formattedDate, value: collectionCount }); // Push the data into the array with the current collectionCount
        }

        //////////// days with no collection////////////
        daysWithNoCollectionCount =
          daysWithNoCollectionCount +
          this.calculateDaysBetweenDates(previousDate, currentDate); //calculate days with no collection
        noCollectionsCount.push(daysWithNoCollectionCount); //add days with no collection for min/max values
        noCollectionCountArray.push({
          name: totalCollections,
          value: daysWithNoCollectionCount,
        }); //push data to array for graphs
        daysWithNoCollectionCount = 0; //reset count for next itteration
        //////////// days with no collection////////////
      } else {
        // Push the first entry into the array
        barChartRow.push({ name: formattedDate, value: collectionCount });
      }

      ////////////////// bin capacity per collection ////////////////////////////////////
      const collectedQuantity = Number(
        (
          ((this.binData.bin_height - currentRow.preceding_dst) /
            this.binData.bin_height) *
          100
        ).toFixed(2)
      ); //calculate collected volume

      this.collections.push({
        name: currentDate.toLocaleDateString(this.charts.getDateLanguage(), {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
        value: collectedQuantity,
      }); //push data to array for graphs
      ////////////////// bin capacity per collection ////////////////////////////////////
    }
    ////////// total waste collected calculation //////////////////////

    const cubicMetersOfWaste =
      this.calculateCubicMeters(
        this.binData.bin_volume,
        JSON.parse(res.counts)[0].avg_preceding_dst,
        this.binData.bin_height
      ) * this.collections.length;
    this.tonnage =
      this.calculateWasteCollection(
        this.binData.bin_usage,
        cubicMetersOfWaste
      ) * 0.001;

    ////////// total waste collected calculation //////////////////////

    const counts: { [key: string]: number } = {}; // to store counts for each month
    barChartRow.forEach((item) => {
      //for each item in barChartRow
      const name = item.name; //get the name
      counts[name] = (counts[name] || 0) + 1; //increment count by 1 for matching month or initalize it to 0 if it doesn't exist
    });
    const filteredArray = Object.keys(counts).map((name) => ({
      name,
      value: counts[name],
    })); //new array of object with unique month and their count

    const missingMonths = this.findMissingMonths(start, end, collectionData); // get the month(s) missing from collection data set

    for (let monthName of missingMonths) {
      //for each month in the missing months array
      filteredArray.push({ name: monthName, value: 0 }); //add the months with value of 0
    }

    // Sort the filteredArray array chronologically
    filteredArray.sort((a, b) => {
      const dateA = new Date(a.name);
      const dateB = new Date(b.name);
      return dateA.getTime() - dateB.getTime();
    });

    //translate month name to the current language for graphics
    for (let monthData of filteredArray) {
      monthData.name = new Date(monthData.name).toLocaleDateString(
        this.charts.getDateLanguage(),
        {
          year: 'numeric',
          month: 'short',
        }
      );
    }
    barChartRow = filteredArray; //updates monthly collection chart array with correct data

    this.charts.singleBarChartAdmin = barChartRow; // Set monthly collection chart
    this.charts.singleBarChartCollections = this.collections; // set capacity per collection chart
    this.charts.singleBarChart = noCollectionCountArray; // set days without collection chart
    this.totalCollections = totalCollections; //set the total number of collections
    this.totalWasteCollected = Number(this.tonnage.toFixed(2)); //set the total waste collected
    this.maxDaysWithNoCollections = Math.max(...noCollectionsCount); //set maximum days without collection
    this.minDaysWithNoCollections = Math.min(...noCollectionsCount); //set minimum days without collection
    this.binThreshold = `${JSON.parse(res.bins)[0].threshold}%`; //set threshold alert level

    this.charts.showLegendBarChart = false;
    this.charts.viewBarChart = [600, 450];
    this.charts.viewBarChartCollections = [600, 450];
    this.charts.showLegendBarChartCollections = false;
    this.charts.showXAxisBarChartCollections = true;
  }

  /**
   * Function that initialize the data for battery and vcc rate.
   *
   */
  async initBatAndVccLineChart() {
    //Get Translated Labels for Y axis of both Line Charts
    const yAxisLabelBat = await this.charts.getChartLabel('yAxisLineChartBat');
    const yAxisLabelVcc = await this.charts.getChartLabel('yAxisLineChartVcc');

    //Set Line Chart Default dimensions
    this.charts.viewLineChartAdmin = [600, 450];

    //X Axis Label
    const start = new Date(this.startDate);
    start.setUTCHours(5, 0, 0, 0);
    const end = new Date(this.endDate);
    end.setUTCHours(5, 0, 0, 0);

    //formats the date if the X axis dd-months-yyyy
    this.charts.xAxisLabelBatLineChartAdmin = `${start.toLocaleDateString(
      this.charts.getDateLanguage(),
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
    )} - ${end.toLocaleDateString(this.charts.getDateLanguage(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })}`;

    //Loop that recuperates the battery and vcc values
    for (let row of this.reports.historyArray) {
      //Format Timestamps
      const dateBat = this.formatDate(row.bat_timestamp);
      const dateVcc = this.formatDate(row.vcc_timestamp);

      //If battery and Vcc timestamp are within time range
      if (this.isDateIn(dateBat) && this.isDateIn(dateVcc)) {
        //Formats battery and vcc timestamps dd-mm-yyyy
        const formattedDateBat = dateBat.toLocaleDateString(
          this.charts.getDateLanguage(),
          {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }
        );
        const formattedDateVcc = dateVcc.toLocaleDateString(
          this.charts.getDateLanguage(),
          {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }
        );

        //Initialize data series for battery and vcc line charts
        this.seriesBat.push({
          name: formattedDateBat,
          value: row.bat,
        });
        this.seriesVcc.push({
          name: formattedDateVcc,
          value: row.vcc,
        });
      }
    }
    //recuperarate min max values
    let minMax = this.charts.getYScaleMinMaxValue(this.reports.historyArray);

    //Set battery chart Y Axis min and max values
    let min = minMax[0] - 1;
    let max = minMax[1] + 1;
    this.charts.yScaleMinBat = min;
    this.charts.yScaleMaxBat = max;

    //Set vcc chart Y Axis min and max values
    min = minMax[2] - (minMax[2] - Math.floor(minMax[2]));
    max = minMax[3] + (minMax[3] - Math.floor(minMax[3]));
    this.charts.yScaleMinVcc = min;
    this.charts.yScaleMaxVcc = max;

    //Compile battery and vcc Line Chart Data
    this.charts.singleLineChartBat = [
      {
        name: this.thingName,
        series: this.seriesBat,
      },
    ];
    this.charts.singleLineChartVcc = [
      {
        name: 'vcc rate of ' + this.thingName,
        series: this.seriesVcc,
      },
    ];

    //Set Y Axis Label for both charts
    this.charts.yAxisLabelBatLineChartAdmin = yAxisLabelBat;
    this.charts.yAxisLabelVccLineChartAdmin = yAxisLabelVcc;
  }

  /**
   * Function that returns months without collection(s) from a collection of report Data for a given range.
   *
   * @param {number}start A string representing the start date of the time range.
   * @param {number}end A string representing the end date of the time range.
   * @param {any[]}collectionData An array of collection Data.
   *
   * @returns An array of strings containing the months without collections under month-year format.
   */
  findMissingMonths(
    start: number,
    end: number,
    collectionData: any[]
  ): string[] {
    //initialize an array  and a Map
    const missingMonths: string[] = [], //to save missing month(s)
      monthsMap: { [key: string]: boolean } = {}; //to save the months with collection(s)

    // Iterate through a collection data set to mark months with collections
    for (const collection of collectionData) {
      //creates and formats the collection date months-years
      const date = new Date(collection.preceding_timestamp * 1000);

      const yearMonth = date.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'short',
      });
      //mark the month with a collection in the months Map
      monthsMap[yearMonth] = true;
    }
    //makes an array of the map and sort it chronolofically
    const monthsArray = Object.entries(monthsMap);
    monthsArray.sort((a, b) => {
      const dateA = new Date(a[0]);
      const dateB = new Date(b[0]);
      return dateA.getTime() - dateB.getTime();
    });
    // Convert the sorted array back into a map
    const sortedMonthsMap = new Map(monthsArray);

    let startDate = new Date(Date.UTC(start * 1000));
    // Normalize to the 1st of the month
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);

    let endDate = new Date(Date.UTC(end * 1000));
    endDate.setDate(1);
    endDate.setHours(0, 0, 0, 0);


    //itterates through the time interval
    while (startDate <= endDate) {
      //formats the date months-year
      const yearMonth = startDate.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'short',
      });
      // If the month is not found in the sorted months map
      if (!sortedMonthsMap.get(yearMonth)) {
        //add it to the missingMonths array
        missingMonths.push(yearMonth);
      }
      // set day to the first to get all months
      startDate.setDate(1);
      // Move to the next month for itteration
      startDate.setMonth(startDate.getMonth() + 1);
    }
    //returns the array of missing months
    return missingMonths;
  }

  // Function that return true/false depend if fillRate is less then 20
  fillRate(fillRate: number) {
    return fillRate > 20;
  }

  // Function that return difference between two dates
  calculateDaysBetweenDates(startDate: Date, endDate: Date): number {
    // Convert both dates to UTC to ensure consistent calculations
    const utcStartDate = Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );
    const utcEndDate = Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );

    // Calculate the difference in milliseconds
    const millisecondsPerDay = 1000 * 60 * 60 * 24; // Number of milliseconds in a day
    const differenceInMilliseconds = Math.abs(utcEndDate - utcStartDate);

    // Convert the difference to days
    const daysDifference = Math.floor(
      differenceInMilliseconds / millisecondsPerDay
    );
    return daysDifference;
  }

  //Check if 2 rows are within the same month
  isInSameMonth(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth()
    );
  }

  // Function that return true if date is between start and end date
  isDateIn(date: Date): boolean {
    const start = new Date(this.startDate);
    const end = new Date(this.endDate);

    // Check if the date is after or equal to the start of 2023
    // and before the start of 2024
    return date >= start && date <= end;
  }

  //Convert UTC Unix timestamp(H) to local date-time string
  formatDate(date: number) {
    const formattedDate = new Date(date * 1000);
    return formattedDate;
  }

  // Get the pourcentage and put the color(red, yellow or green) for the batery of the selected device
  getBatteryPercentage(battery: number) {
    if (!battery) {
      return 'red';
    }

    let percentage = 0;
    let inCharge = false;
    // Will put this.pourcentage at zero if the pourcentage received is null or not a number
    if (battery !== 0 || battery !== null || battery !== undefined) {
      // Convert the max voltage of the batery(3.7V) in pourcentage 0% to 100%
      percentage = Math.round((battery / 3.7) * 100);
    } else {
      percentage = 0;
    }
    // If the batery is more then 3.7V it's because she is in charging mode
    if (battery > 3.7) {
      percentage = 100;
      inCharge = true;
    }

    const element = document.getElementById('battery');
    // Put the good color for the text depend on the pourcentage of the battery
    switch (true) {
      case percentage >= 0 && percentage <= 30:
        this.color = 'red';
        break;

      case percentage >= 31 && percentage <= 60:
        this.color = 'yellow';
        break;

      case percentage >= 61:
        this.color = 'green';
        break;

      default:
        this.color = 'red';
        break;
    }
    if (element) {
      element.style.color = this.color;
    }
    return percentage;
  }

  // Function that will calculate the volume if all field are well filled
  calculateVolume(shape: string, width: number, height: number, depth: number) {
    // Calculate the volume of a rectangular box in liters
    if (shape === 'S' && width > 0 && height > 0 && depth > 0) {
      const volumeCubicMM = width * height * depth;
      const volumeLiters = (volumeCubicMM / 1000000).toFixed(2);

      return volumeLiters + ' L';
    }

    // Calculate the volume of a cylinder in liters
    if (shape === 'C' && width > 0 && height > 0) {
      const radius = width / 2;
      const volumeCubicMM = Math.PI * Math.pow(radius, 2) * height;
      const volumeLiters = (volumeCubicMM / 1000000).toFixed(2);

      return volumeLiters + ' L';
    }

    return '';
  }

  // Function that will calculate the volume if all field are well filled
  calculateCubicMeters(binVolume: number, dst: number, binHeight: number) {
    const multiplier = 1 - dst / binHeight;
    return (binVolume / 1000) * multiplier;
  }

  //Calculate the total waste collected
  calculateWasteCollection(
    usage: string,
    totalCubicMetersOfWaste: number
  ): number {
    const weightPerCubicMeter = this.getWeightByValue(usage);
    if (!isNaN(totalCubicMetersOfWaste) && weightPerCubicMeter !== undefined) {
      return totalCubicMetersOfWaste * weightPerCubicMeter;
    } else {
      return 0;
    }
  }

  //Get the average weight for the bin usage type
  getWeightByValue(value: string): number {
    const bin = BinUsage.find((bin) => bin.value === value);
    return bin?.weight || 0;
  }

  //Displays the correct label for the Device Health row in the Device Information element
  displayHealth(thingName: string, timestamp: number, bat: string) {
    if (thingName === '') {
      return 'Inactive';
    }
    //Check Health Statistics
    const lastUpdate = new Date(timestamp * 1000);
    const battery: number = (parseFloat(bat) / 3.7) * 100;
    switch (true) {
      case this.isDateGapGreaterThan48Hours(lastUpdate) &&
        this.isBinIdPopulated(thingName, this.relationships) &&
        battery < 40:
        return 'Unhealthy';
      case !this.isDateGapGreaterThan48Hours(lastUpdate) &&
        this.isBinIdPopulated(thingName, this.relationships) &&
        battery > 40:
        return 'Healthy';
      default:
        return 'Inactive';
    }
  }

  //Return Label
  translateStatus(status: number) {
    if (status === 1) {
      return 'active';
    } else {
      return 'inactive';
    }
  }

  // Function that check if difference between dateToCompare and the current date is greater then 48 hours
  isDateGapGreaterThan48Hours(dateToCompare: Date): boolean {
    // Get the current date and time
    const currentDate = new Date();

    // Calculate the time difference in milliseconds
    const timeDifference = currentDate.getTime() - dateToCompare.getTime();

    // Convert the time difference to hours
    const hoursDifference = timeDifference / (1000 * 60 * 60);

    // Check if the gap is greater than 48 hours
    return hoursDifference > 48;
  }

  // Function that check in BinArray if the thing name is in one of the bin
  isBinIdPopulated(thingName: string, array: BinArrayItem[]): boolean {
    const matchingItem = array.find((item) => item.thing_name === thingName);

    return !!matchingItem && !!matchingItem.bin_id;
  }

  // Function that get all device relationships
  getRelationships() {
    const url =
      environment.api.stage + environment.api.route.getDeviceRelationships;

    return this.http.get(url).pipe(
      map((response) => {
        if (response) {
          for (const row of JSON.parse(JSON.stringify(response))) {
            this.relationships.push({
              thing_name: row.thing_name,
              bin_id: row.bin_id,
            });
          }
        }
      }),
      catchError((error) => {
        console.error('API Error:', error);
        throw error; // Re-throw the error for the calling code to handle
      })
    );
  }

  //Apply filter preferences
  applyFilter() {
    window.location.href = `/device-statistics/${this.thingName}/${this.startDate}/${this.endDate}`;
  }
  /**
   * Function that export data to a downloadable CSV file.
   *
   * @param {string} thingName A string representing the name of the device to export data.
   * @param {number} option A number indicating which CSV file to export: 1 for history, 2 for bin report, 3 for collection report
   */
  async exportCSVReports(thingName: string, option: number) {
    const binUsage = await this.charts.getChartLabel(
      this.bins.getBinUsage(this.binData.bin_usage)
    );
    const battery =
      this.getBatteryPercentage(
        this.reports.historyArray[this.reports.historyArray.length - 1].bat
      ).toString() + '%';
    const health = this.displayHealth(
      this.thingName,
      this.timestamp,
      this.bat
    );
    const translatedHealth = await this.charts.getChartLabel(health);
    const deviceReport: any = [];
    const collectionsArray: any = [];
    const deviceInformation: any = [];
    const startDate = this.formatDateISO8601(this.startDate);
    const endDate = this.formatDateISO8601(this.endDate);

    switch (this.charts.getDateLanguage()) {
      case 'en-CA':
        deviceInformation.push({
          'Device Name': this.thingName,
          'Alert Threshold': this.binThreshold,
          'Start Date': startDate,
          'End Date': endDate,
          'Bin Height': this.binData.bin_height,
          'Bin Volume': this.binData.bin_volume,
          'Device Health': translatedHealth,
          Battery: battery,
          Usage: binUsage,
          'Collected Waste (tonnes)': this.tonnage.toFixed(2),
          Collections: this.totalCollections,
        });

        for (const row of this.collections) {
          collectionsArray.push({
            Device: thingName,
            'Collection Date': row.name,
            'Percentage Filled': row.value,
            Location: this.binData.bin_address,
          });
        }

        break;
      case 'es-MX':
        deviceInformation.push({
          Dispositivo: this.thingName,
          'Umbral de alerta': this.binThreshold,
          'Fecha de inicio': startDate,
          'Fecha final': endDate,
          'Altura del contenedor': this.binData.bin_height,
          'Volumen del contenedor': this.binData.bin_volume,
          'salud del dispositivo': translatedHealth,
          Batería: battery,
          Uso: binUsage,
          'Toneladas recolectadas': this.tonnage.toFixed(2),
          Colecciones: this.totalCollections,
        });

        for (const row of this.collections) {
          collectionsArray.push({
            Dispositivo: thingName,
            'Fecha de colecció': row.name,
            'Porcentaje llenado': row.value,
            Ubicación: this.binData.bin_address,
          });
        }
        break;
      default:
        deviceInformation.push({
          'Nom du dispositif': this.thingName,
          "Seuil d'alerte": this.binThreshold,
          'Date de début': startDate,
          'Date de fin': endDate,
          'Hauteur de la benne (mm)': this.binData.bin_height,
          'Volume de la benne': this.binData.bin_volume,
          "État de l'appareil": translatedHealth,
          Batterie: battery,
          "Type d'utilisation": binUsage,
          'Déchets collectés (tonnes)': this.tonnage.toFixed(2),
          'collections totales': this.totalCollections,
        });

        for (const row of this.collections) {
          collectionsArray.push({
            Dispositif: thingName,
            'Date de collections': row.name,
            'Pourcentage rempli': row.value,
            Emplacement: this.binData.bin_address.toString(),
          });
        }
        break;
    }

    const historyArray = this.reports.historyArray;

    for (const row of historyArray) {
      const date = new Date(row.dst_timestamp * 1000);
      if (row.timestamp.toString().length === 10) {
        row.timestamp = new Date(row.timestamp * 1000);
      } else {
        row.timestamp = new Date(parseInt(row.timestamp));
      }

      if (this.isDateIn(date)) {
        row.img = 'N/A';
        row.fdv_timestamp = new Date(row.fdv_timestamp * 1000);
        row.img_timestamp = new Date(row.img_timestamp * 1000);
        row.tmp_timestamp = new Date(row.tmp_timestamp * 1000);
        row.vcc_timestamp = new Date(row.vcc_timestamp * 1000);
        row.sig_timestamp = new Date(row.sig_timestamp * 1000);
        row.pre_timestamp = new Date(row.pre_timestamp * 1000);
        row.bat_timestamp = new Date(row.bat_timestamp * 1000);
        row.tm1_timestamp = new Date(row.tm1_timestamp * 1000);
        row.tm2_timestamp = new Date(row.tm2_timestamp * 1000);
        row.dst_timestamp = new Date(row.dst_timestamp * 1000);
        row.tm0_timestamp = new Date(row.tm0_timestamp * 1000);
        row.nct_timestamp = new Date(row.nct_timestamp * 1000);
        row.lfr_timestamp = new Date(row.lfr_timestamp * 1000);
        row.hum_timestamp = new Date(row.hum_timestamp * 1000);
        row.voc_timestamp = new Date(row.voc_timestamp * 1000);
        row.hib_timestamp = new Date(row.hib_timestamp * 1000);

        deviceReport.push(row);
      }
    }
    switch (option) {
      case 1: //history report
        this.systemMessage.selectRibbon('info', 'openCSV');
        this.csv.exportToCsv(
          deviceReport,
          `${thingName}-History-${this.startDate.replaceAll(
            '-',
            ''
          )}-${this.endDate.replaceAll('-', '')}.csv`
        );
        break;
      case 2: //bin report
        this.systemMessage.selectRibbon('info', 'openCSV');
        this.csv.exportToCsv(
          deviceInformation,
          `${thingName}-bin-report-${this.startDate.replaceAll(
            '-',
            ''
          )}-${this.endDate.replaceAll('-', '')}.csv`
        );
        break;
      case 3: //collection report
        this.systemMessage.selectRibbon('info', 'openCSV');
        this.csv.exportToCsv(
          collectionsArray,
          `${thingName}-collections-report-${this.startDate.replaceAll(
            '-',
            ''
          )}-${this.endDate.replaceAll('-', '')}.csv`
        );
        break;
    }
  }

  //Unused
  printElementContent() {
    const element = document.getElementById('device-statistics');

    if (element) {
      const content = element.outerHTML;
      const printWindow = window.open('', '_blank');

      if (printWindow) {
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.print();
      } else {
        console.error('Failed to open print window');
      }
    } else {
      console.error(`Element with ID device-statistics not found`);
    }
  }

  // Event listener for window resize
  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    const currentScreenWidth = this.getScreenWidth();
    const currentScreenHeight = this.getScreenHeight();
    // Check the screen width and update the isSmallScreen flag accordingly
    this.isSmallScreenWidth = currentScreenWidth <= 859;
    //Adjust chart parameters for view size
    this.adjustView(currentScreenWidth, currentScreenHeight);
  }

  // Function to get the current screen width
  getScreenWidth(): number {
    // Return the inner width of the window as the screen width
    return window.innerWidth;
  }

  // Funciton to get the current screen height
  getScreenHeight(): number {
    // Retunr the current screen height of the window
    return window.innerHeight;
  }

  //UI Functions
  adjustView(currentScreenWidth: number, currentScreenHeight: number) {
    const widthPercentage = currentScreenWidth / 100;
    const heightPercentage = currentScreenHeight / 100;
    // Set the charts depend on current width screen
    switch (true) {
      case currentScreenWidth >= 1400:
        this.charts.viewLineChartAdmin = [600, 450];
        this.charts.viewBarChart = [600, 450];
        this.charts.viewBarChartAdmin = [600, 450];
        this.charts.viewBarChartCollections = [600, 450];
        break;
      case currentScreenWidth >= 1200 && currentScreenWidth < 1400:
        this.charts.viewLineChartAdmin = [1000, 550];
        this.charts.viewBarChart = [
          widthPercentage * 52,
          heightPercentage * 40,
        ];
        this.charts.viewBarChartCollections = [
          widthPercentage * 52,
          heightPercentage * 40,
        ];
        this.charts.viewBarChartAdmin = [
          widthPercentage * 52,
          heightPercentage * 40,
        ];
        break;
      case currentScreenWidth > 915 && currentScreenWidth < 1200:
        this.charts.viewLineChartAdmin = [
          widthPercentage * 52,
          heightPercentage * 40,
        ];
        this.charts.viewBarChart = [
          widthPercentage * 52,
          heightPercentage * 40,
        ];
        this.charts.viewBarChartCollections = [
          widthPercentage * 52,
          heightPercentage * 40,
        ];
        this.charts.viewBarChartAdmin = [
          widthPercentage * 52,
          heightPercentage * 40,
        ];
        break;
      case currentScreenWidth <= 915 && currentScreenWidth > 800:
        this.charts.viewLineChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 60,
        ];
        this.charts.viewBarChart = [
          widthPercentage * 60,
          heightPercentage * 60,
        ];
        this.charts.viewBarChartCollections = [
          widthPercentage * 60,
          heightPercentage * 60,
        ];
        this.charts.viewBarChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 60,
        ];
        break;
      case currentScreenWidth <= 800 && currentScreenWidth > 575:
        this.charts.viewLineChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 50,
        ];
        this.charts.viewBarChart = [
          widthPercentage * 60,
          heightPercentage * 50,
        ];
        this.charts.viewBarChartCollections = [
          widthPercentage * 60,
          heightPercentage * 50,
        ];
        this.charts.viewBarChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 50,
        ];
        break;
      case currentScreenWidth <= 700 && currentScreenWidth > 600:
        this.charts.viewLineChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 50,
        ];
        this.charts.viewBarChart = [
          widthPercentage * 60,
          heightPercentage * 50,
        ];
        this.charts.viewBarChartCollections = [
          widthPercentage * 60,
          heightPercentage * 50,
        ];
        this.charts.viewBarChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 50,
        ];
        break;
      case currentScreenWidth <= 600 && currentScreenWidth > 500:
        this.charts.viewLineChartAdmin = [400, 200];
        this.charts.viewBarChart = [
          widthPercentage * 62,
          heightPercentage * 42,
        ];
        this.charts.viewBarChartCollections = [
          widthPercentage * 62,
          heightPercentage * 42,
        ];
        this.charts.viewBarChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 40,
        ];
        break;
      case currentScreenWidth <= 500:
        this.charts.viewLineChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 30,
        ];
        this.charts.viewBarChart = [
          widthPercentage * 60,
          heightPercentage * 30,
        ];
        this.charts.viewBarChartCollections = [
          widthPercentage * 60,
          heightPercentage * 30,
        ];
        this.charts.viewBarChartAdmin = [
          widthPercentage * 60,
          heightPercentage * 30,
        ];
        break;
    }
  }
  /**
   * Function that checks if a given number is finite.
   *
   * @param value A number.
   * @returns a boolean indicating if the given value is finite .
   */
  isFiniteNumber(value: number): boolean {
    return Number.isFinite(value);
  }

  /**
   * Function that formats a given date to ISO8601 standard.
   *
   * @param date A string representing the date to format.
   * @returns a string of the date with format yyyy-mm-dd.
   */
  formatDateISO8601(date: string): string {
    const dateToFormat = new Date(date);
    const year = dateToFormat.getFullYear();
    const month = String(dateToFormat.getMonth() + 1).padStart(2, '0');
    const day = String(dateToFormat.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Function that disable or able a DOM element based on returned data result.
   *
   * @param element A reference of the DOM element to toggle the class.
   */
  toggleDisableClass(element: ElementRef) {
    //saves the native element
    const nativeElement = element.nativeElement;
    switch (
      this.returnedResult //checks boolean for returned results
    ) {
      case true: // results returned
        //removes the disabled class if its there and result were returned
        if (nativeElement.classList.contains('disabled'))
          this.renderer.removeClass(element.nativeElement, 'disabled');
        break;
      case false: //no results returned
        //adds the disabled class if its not there and result were not returned
        if (!nativeElement.classList.contains('disabled'))
          this.renderer.addClass(element.nativeElement, 'disabled');
        break;
    }
  }

  /*
function had been modified to return to previous place based on a variable in session storage. For it to work
you need to set a session Item named toDeviceStats with the url of the component its comming from as a value.
This is done thanks to a function called setNavToDeviceStat that is called when a link to device-statisitcs is clicked
(handles rerouting and setting the session variable).
  toPreviousPage will check if the session variable has a value and will navigate to /value if it's valid.
  Otherwise it returns to /iot
  */
  toPreviousPage() {
    const previousRoute = sessionStorage.getItem('toDeviceStats');
    if (previousRoute != ' ' && previousRoute != null) {
      this.router.navigate([`/${previousRoute}`]);
      sessionStorage.removeItem('toDeviceStats');
    } else {
      this.router.navigate(['/iot']);
    }
  }

  /**
   * Toggles betweent the view of the Battery Rate and VCC Rate grahs.
   *
   * @param mode A number eitheir 1 0r 0 that determine which graphs to show
   */
  toggleLineChart(mode: number) {
    //recuperate the native elements of the view of the graphs
    const batNative = this.batteryRef.nativeElement; //battery
    const vccNative = this.VCCRef.nativeElement; //vcc

    switch (mode) {
      case 0: //display battery
        batNative.style.display = 'block'; //battery graphs visible
        vccNative.style.display = 'none'; //vcc graphs invisible
        //handles the color change to show which graphs is selected
        this.renderer.removeClass(
          document.getElementById('vcc'),
          'selectedChart'
        );
        this.renderer.addClass(document.getElementById('bat'), 'selectedChart');
        break;
      case 1: //display vcc
        batNative.style.display = 'none'; //battery graphs invisible
        vccNative.style.display = 'block'; //battert graphs visible
        //handles the color change to show which graphs is selected
        this.renderer.removeClass(
          document.getElementById('bat'),
          'selectedChart'
        );
        this.renderer.addClass(document.getElementById('vcc'), 'selectedChart');
    }
  }

  getDisabledButtonStyle() {
    if (!this.validFillRate) {
      return {
        'pointer-events': 'none!important',
        'background-color': 'lightgray!important',
        cursor: 'not-allowed!important',
        'border-color': 'green',
      };
    }
    return '';
  }
}
